import { FC } from 'react';

import { GiftCsMemoLogItem } from '@shared/api/giftCsMemo/giftCsMemo.interface';
import { BucketImage } from '@shared/components/BucketImage';
import { XIcon } from '@heroicons/react/outline';

//* 쿠폰 수령자 상세 — "이 사람이 진짜 받은 사람 맞냐"에 답하는 근거 묶음.
//*
//* 쿠폰은 구매자가 링크를 공유하고 **받은 사람이 자기 번호로 인증하고 서명해서** 신청한다.
//* 그래서 이름보다 ① 구매자가 남긴 메모(누구에게 보낸 건지) ② 번호 인증 기록 ③ 서명
//* 세 가지가 실제 응대 근거다. 표에 다 넣으면 열이 터지므로 여기로 접는다.

const pad = (n: number) => String(n).padStart(2, '0');
const fmt = (date: Date | string | undefined) => {
  if (!date) return '';
  const d = new Date(date);
  return `${String(d.getFullYear()).slice(2)}.${pad(d.getMonth() + 1)}.${pad(
    d.getDate(),
  )} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fmtPhone = (phone: string | undefined) => {
  if (!phone) return '';
  const n = phone.replace(/[^0-9]/g, '');
  return n.length === 11 ? `${n.slice(0, 3)}-${n.slice(3, 7)}-${n.slice(7)}` : phone;
};

const Row: FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="grid grid-cols-[64px_1fr] gap-2 py-1 text-xs">
    <span className="text-gray-400">{label}</span>
    <span className="break-all text-gray-800">{children}</span>
  </div>
);

export interface CouponRecipientModalProps {
  item: GiftCsMemoLogItem;
  onClose: () => void;
}

export const CouponRecipientModal: FC<CouponRecipientModalProps> =
  function CouponRecipientModal({ item, onClose }) {
    const coupon = item.coupon;
    const verification = coupon?.verification;
    //* 인증 번호와 신청 번호가 다르면 "대신 신청해준 사람" 이거나 오기입이다 — 눈에 띄어야 한다
    const phoneMismatch =
      !!verification?.phoneNumber &&
      !!coupon?.recipientPhoneNumber &&
      verification.phoneNumber.replace(/[^0-9]/g, '') !==
        coupon.recipientPhoneNumber.replace(/[^0-9]/g, '');

    return (
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/40" onClick={onClose} />
        <div className="relative flex max-h-[85vh] w-full max-w-sm flex-col overflow-auto rounded-xl bg-white shadow-2xl">
          <div className="flex items-start justify-between gap-3 px-5 py-3">
            <div className="min-w-0">
              <h3 className="text-sm font-bold text-gray-900">수령자 정보</h3>
              <p className="mt-0.5 truncate text-xs text-gray-500">
                {coupon?.fullCode ?? ''} {coupon?.itemName ?? ''}
              </p>
            </div>
            <button
              onClick={onClose}
              className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <XIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="space-y-3 px-5 pb-5">
            <div className="rounded-lg border px-3 py-2">
              {/* 이름이 없는 건이 많아 메모가 사실상 이름 자리다 */}
              <Row label="메모">
                {coupon?.memo || <span className="text-gray-300">구매자 메모 없음</span>}
              </Row>
              <Row label="이름">
                {coupon?.recipientName || (
                  <span className="text-gray-300">신청 전 · 이름 없음</span>
                )}
              </Row>
              <Row label="연락처">
                {fmtPhone(coupon?.recipientPhoneNumber) || (
                  <span className="text-gray-300">-</span>
                )}
              </Row>
              <Row label="신청">
                {fmt(coupon?.requestedAt) || <span className="text-gray-300">-</span>}
              </Row>
            </div>

            <div className="rounded-lg border px-3 py-2">
              <p className="mb-1 text-[11px] font-semibold text-gray-700">번호 인증</p>
              {verification?.verifiedAt || verification?.phoneNumber ? (
                <>
                  <Row label="인증번호">{fmtPhone(verification.phoneNumber)}</Row>
                  <Row label="인증시각">{fmt(verification.verifiedAt)}</Row>
                  {phoneMismatch && (
                    <p className="mt-1 rounded bg-amber-50 px-2 py-1 text-[11px] text-amber-700">
                      인증한 번호와 신청 연락처가 다릅니다 — 대신 신청했거나 오기입일 수
                      있습니다.
                    </p>
                  )}
                </>
              ) : (
                <p className="py-1 text-xs text-gray-400">
                  인증 기록 없음 (관리자 대리발송이면 정상입니다)
                </p>
              )}
            </div>

            <div className="rounded-lg border px-3 py-2">
              <p className="mb-1 text-[11px] font-semibold text-gray-700">서명</p>
              {coupon?.signatureImageUrl ? (
                //* 서명은 작게 올라가 축소본(w400)이 아예 없는 경우가 흔하다 → 원본으로 띄운다
                <BucketImage
                  value={coupon.signatureImageUrl}
                  size="raw"
                  className="rounded border bg-white"
                  alt="수령자 서명"
                />
              ) : (
                <p className="py-1 text-xs text-gray-400">
                  서명 없음 (대리발송 건은 서명을 받지 않습니다)
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };
